import { useCallback, useEffect } from 'react'

import { signIn } from '@circlefin/auth'
import { signIn as signInSection } from '@services/sections/lib/home'
import { useSegment, SegmentEvents } from '@services/segment'
import { useTransComponent } from '@shared/hooks/component'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'

import { ActivateUserFormExisting } from './ActivateUser.Form.Existing'
import { ActivateUserFormNew } from './ActivateUser.Form.New'

import type { SignInCredential, UserActivation } from '@shared/graphql'

export interface ActivateUserFormProps
  extends Pick<
    UserActivation,
    | 'email'
    | 'existingUser'
    | 'firstName'
    | 'lastName'
    | 'stateToken'
    | 'institutionName'
  > {
  /**
   * State token.
   */
  stateToken: string
}

export const ActivateUserForm: React.FC<ActivateUserFormProps> = ({
  email,
  institutionName,
  existingUser,
  ...rest
}) => {
  const { t } = useTranslation('onboard/common')
  const router = useRouter()
  const { trackLinkClick, track, page, identify } = useSegment()

  const handleSubmit = useCallback(
    async (credential?: SignInCredential | null) => {
      try {
        track(SegmentEvents.CreateAccountClicked, {
          props: {
            page: 'Set up account',
          },
          userTraits: {
            email: email ?? undefined,
          },
        })

        // Unresolved keeps the submit button in loading state while we are
        // redirecting the user to the next page to avoid form resubmission.
        await new Promise(() => {
          if (credential?.sessionToken) {
            // If we have a sessionToken, we can can auto-login
            void signIn(
              { legacy: true },
              {
                sessionToken: credential.sessionToken,
              },
            )
          } else {
            // If no sessionToken, fallback to login screen
            void router.push(signInSection.route)
          }
        })
      } catch {
        // Error UI handled in GraphQLErrorBoundary
      }
    },
    [email, router, track],
  )

  const [TermsWithLinks] = useTransComponent('onboard/common', (link, i) => (
    <a
      key={`checkbox-link-${i}`}
      href={link}
      onClick={trackLinkClick}
      rel="noopener noreferrer"
      target="_blank"
    />
  ))

  useEffect(() => {
    if (email) {
      identify({ traits: { email } })
      page({ userTraits: { email } })
    }
  }, [email, identify, page])

  return (
    <div
      className="m-auto max-w-116 text-center text-neutral-subtle"
      data-testid="default-content"
    >
      <div>
        {institutionName && (
          <h1 className="type-intro-lg">
            <span className="text-neutral-subtle">{institutionName}</span>
          </h1>
        )}
        <h2 className="mt-4 type-h-page-sm">{t`accountActivation.activateUser.setupYourAccount`}</h2>
        <p className="mt-4 type-intro-sm">
          {t(
            `accountActivation.activateUser.details.${
              existingUser ? 'existing' : 'new'
            }`,
          )}
        </p>

        {existingUser ? (
          <ActivateUserFormExisting
            {...rest}
            email={email}
            onSubmit={handleSubmit}
          />
        ) : (
          <ActivateUserFormNew
            {...rest}
            email={email}
            onSubmit={handleSubmit}
          />
        )}

        <p className="mt-6 type-body-xs" data-testid="terms">
          <TermsWithLinks
            i18nArrayKey="accountActivation.activateUser.terms.term1.links"
            i18nKey="accountActivation.activateUser.terms.term1.label"
          />
          <br />
          {t`accountActivation.activateUser.terms.term2.label`}
        </p>
      </div>
    </div>
  )
}
